/**
 * Day-context header — the "what's special about today" strip shown atop the
 * Trainer Daily Brief surface `/trainers/:id/today` (the `/today` board page it
 * also topped was retired).
 *
 * Fetches `GET /api/day-context` (London weather + TfL tube status + UK bank
 * holidays, server-cached + fault-tolerant) and renders a headline banner +
 * weather / tube / next-holiday chips. Each widget degrades independently: a
 * widget the server marks `{ available: false }` shows an "unavailable" chip,
 * never an error — this is optional chrome over the client list that matters.
 */

import { useEffect, useState } from "react";
import { api, type DayContextResponse } from "../lib/api";

function reasonsOf(
  ctx: DayContextResponse,
): string | undefined {
  if (!ctx.tube.available) return undefined;
  const rs = ctx.tube.disrupted
    .map((l) => l.reason)
    .filter((r): r is string => r !== null);
  return rs.length > 0 ? rs.join(" ") : undefined;
}

export function DayContextHeader(): JSX.Element | null {
  const [ctx, setCtx] = useState<DayContextResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .dayContext()
      .then((c) => {
        if (!cancelled) setCtx(c);
      })
      .catch(() => {
        // Optional chrome — stay silent on failure rather than block the page.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (ctx === null) return null;

  return (
    <div className="day-context">
      <div className={`day-context-banner dc-${ctx.status}`}>
        <span className="dc-headline">{ctx.headline}</span>
      </div>
      <div className="day-context-chips">
        {ctx.weather.available ? (
          <span
            className="dc-chip dc-weather"
            title={`Wind ${Math.round(ctx.weather.wind_kmh)} km/h · ${
              ctx.weather.precipitation_mm
            } mm precip`}
          >
            {ctx.weather.emoji} {Math.round(ctx.weather.temp_c)}°C ·{" "}
            {ctx.weather.label}
            {ctx.weather.temp_max_c !== null &&
            ctx.weather.temp_min_c !== null ? (
              <span className="dc-muted">
                {" "}
                (↑{Math.round(ctx.weather.temp_max_c)}° ↓
                {Math.round(ctx.weather.temp_min_c)}°)
              </span>
            ) : null}
          </span>
        ) : (
          <span className="dc-chip dc-unavailable">Weather unavailable</span>
        )}

        {ctx.tube.available ? (
          ctx.tube.all_good ? (
            <span className="dc-chip dc-tube-good">🚇 Tube: Good service</span>
          ) : (
            <span className="dc-chip dc-tube-bad" title={reasonsOf(ctx)}>
              🚇{" "}
              {ctx.tube.disrupted
                .map((l) => `${l.name} (${l.status})`)
                .join(", ")}
            </span>
          )
        ) : (
          <span className="dc-chip dc-unavailable">Tube status unavailable</span>
        )}

        {ctx.special_day.available &&
        !ctx.special_day.is_holiday_today &&
        ctx.special_day.next_holiday !== null ? (
          <span className="dc-chip dc-holiday-next">
            📅 Next bank holiday: {ctx.special_day.next_holiday.title} (
            {ctx.special_day.next_holiday.date})
          </span>
        ) : null}
      </div>
    </div>
  );
}
